import { useState, useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";

const pad = (n) => String(n).padStart(2, "0");
const diff = (target) => Math.max(0, new Date(target).getTime() - Date.now());

// Rotating countdown across the upcoming races. Pass `races: [{label, date}]`.
// Falls back to a single `target`+`label` if no races array is given.
export const Countdown = ({ races, target, label, interval = 10000 }) => {
  // Only future races, soonest first.
  const list = useMemo(() => {
    const base = races && races.length ? races : [{ label, date: target }];
    return base
      .filter((r) => new Date(r.date).getTime() > Date.now())
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [races, target, label]);

  const [idx, setIdx] = useState(0);
  const [t, setT] = useState(0);

  // rotate through races
  useEffect(() => {
    if (list.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % list.length), interval);
    return () => clearInterval(id);
  }, [list, interval]);

  // keep idx valid if list shrinks
  useEffect(() => { setIdx((i) => (i >= list.length ? 0 : i)); }, [list.length]);

  // tick the current race every second
  const current = list[idx];
  useEffect(() => {
    if (!current) return;
    const update = () => setT(diff(current.date));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [current]);

  if (!current) return null;

  const days = Math.floor(t / 86400000);
  const hours = Math.floor((t % 86400000) / 3600000);
  const mins = Math.floor((t % 3600000) / 60000);
  const secs = Math.floor((t % 60000) / 1000);

  const Cell = ({ v, l }) => (
    <div className="text-center min-w-[52px]">
      <span className="font-display text-3xl md:text-4xl font-bold text-[#BAF173] tabular-nums leading-none">{pad(v)}</span>
      <span className="block text-[10px] uppercase tracking-widest text-white/40 mt-1.5">{l}</span>
    </div>
  );
  const Sep = () => <span className="font-display text-2xl md:text-3xl text-white/20 pb-4">:</span>;

  return (
    <div
      data-testid="race-countdown"
      className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 border border-white/10 bg-[#0A0A0A] px-6 py-5"
    >
      <div className="flex items-center gap-2.5 sm:w-64">
        <span className="h-2.5 w-2.5 rounded-full bg-[#BAF173] animate-pulse shrink-0" />
        <div className="min-h-[36px] flex items-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={current.label}
              data-testid="countdown-race-label"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4 }}
              className="font-display text-xs uppercase tracking-widest text-white/60 leading-tight"
            >
              Next race<br /><span className="text-white/90">{current.label}</span>
            </motion.p>
          </AnimatePresence>
        </div>
      </div>

      {current.tbc ? (
        <div className="flex items-end">
          <span className="font-display text-3xl md:text-4xl font-bold text-[#BAF173] tracking-[0.15em]">TBC</span>
          <span className="ml-3 pb-1 text-[10px] uppercase tracking-widest text-white/40">Date to be confirmed</span>
        </div>
      ) : (
        <div className="flex items-end gap-3 sm:gap-4">
          <Cell v={days} l="Days" />
          <Sep />
          <Cell v={hours} l="Hrs" />
          <Sep />
          <Cell v={mins} l="Min" />
          <Sep />
          <Cell v={secs} l="Sec" />
        </div>
      )}

      {list.length > 1 && (
        <div className="flex gap-1.5 sm:ml-auto" data-testid="countdown-dots">
          {list.map((r, i) => (
            <button
              key={r.label}
              onClick={() => setIdx(i)}
              aria-label={`Show ${r.label}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-[#BAF173]" : "w-1.5 bg-white/25 hover:bg-white/50"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
